import { useEffect } from "react";
import { Link } from "react-router-dom";

import { getPrivacyPolicy } from "@/content/legal/privacy";
import { getTermsOfService } from "@/content/legal/terms";
import { legalLink, useBrand } from "@/shared/brand";
import type { LegalBlock, LegalDoc } from "@/content/legal/types";

/**
 * 《用户服务协议》/《隐私政策》公开页 —— 不登录也能看(登录页底部链过来),
 * 所以不包 ProtectedRoute/AdminLayout。正文全在 content/legal,这里只管排版。
 */
// OEM 贴牌:正文按当前品牌的 legal 主体现算(直营 = 原三常量,逐字不变)。
const DOCS = { terms: getTermsOfService, privacy: getPrivacyPolicy } as const;

export type LegalDocKind = keyof typeof DOCS;

function Block({ block }: { block: LegalBlock }) {
  if (typeof block === "string") return <p>{block}</p>;
  return (
    <ul className="list-disc space-y-1.5 pl-5">
      {block.list.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function LegalDocView({ doc }: { doc: LegalDoc }) {
  return (
    <article className="space-y-6 text-[15px] leading-7 text-foreground/90">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground sm:text-3xl">{doc.title}</h1>
        <p className="text-sm text-muted-foreground">更新日期：{doc.updatedAt}</p>
      </header>
      <div className="space-y-3">
        {doc.intro.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </div>
      {doc.sections.map((s) => (
        <section key={s.heading} className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">{s.heading}</h2>
          {s.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </section>
      ))}
    </article>
  );
}

export default function LegalDocPage({ kind }: { kind: LegalDocKind }) {
  const other: LegalDocKind = kind === "terms" ? "privacy" : "terms";
  const brand = useBrand();
  const vars = { ...brand.legal };
  // OEM(W3 C-F):品牌把协议外置了(termsUrl / privacyUrl)→ 站内旧链接(书签、H5 里发过的)直接转过去,
  // 不再展示站内默认文本(那份的主体口径与渠道外置的协议可能不一致)。
  const external = legalLink(brand, kind);
  useEffect(() => {
    if (external.external) window.location.replace(external.href);
  }, [external.external, external.href]);
  if (external.external) return null;
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-8 sm:py-14">
        <nav className="mb-8 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <Link to="/login" className="text-primary hover:underline">
            返回登录
          </Link>
          {legalLink(brand, other).external ? (
            <a
              href={legalLink(brand, other).href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground"
            >
              {DOCS[other](vars).title}
            </a>
          ) : (
            <Link to={`/legal/${other}`} className="text-muted-foreground hover:text-foreground">
              {DOCS[other](vars).title}
            </Link>
          )}
        </nav>
        <LegalDocView doc={DOCS[kind](vars)} />
      </div>
    </div>
  );
}
